import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Main from '../layouts/Main';
import { useApi } from '../contexts/Api';
import EditLongDescDialog from '../components/EditLongDescDialog';

const LongDescriptions = () => {
  const { 
    longDescriptions, 
    longDescTotal, 
    longDescPage, 
    longDescPageCount,
    loadingLongDesc,
    fetchLongDescriptions,
    deleteLongDescription,
    setLongDescSearchParams
  } = useApi();

  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedSKU, setSelectedSKU] = useState(null);

  const handlePageChange = (event, value) => {
    setLongDescSearchParams('page', value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Search clicked with term:', searchTerm);
    setLongDescSearchParams('q', searchTerm);
    if (searchTerm) {
      setLongDescSearchParams('page', 1);
    }
  };

  const handleEditClick = (desc) => {
    setSelectedDescription(desc);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedDescription(null);
  };

  const handleEditSave = async () => {
    setEditDialogOpen(false);
    setSelectedDescription(null);
    await fetchLongDescriptions();
  };

  const handleDeleteClick = (sku) => {
    setSelectedSKU(sku);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSKU) return;

    setDeleteLoading(true);

    try {
      const success = await deleteLongDescription(selectedSKU);
      if (success) {
        setDeleteDialogOpen(false);
        setSelectedSKU(null);
        await fetchLongDescriptions();
      } else {
        alert('Failed to delete description');
      }
    } catch (error) {
      console.error('Error deleting description:', error);
      alert('Failed to delete description');
    } finally {
      setDeleteLoading(false);
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const hasAnyDescription = (desc) => {
    return desc.longDesc_en || desc.longDesc_fi || desc.longDesc_fr || desc.longDesc_de;
  };

  return (
    <Main>
      <div className="page-header">
        <div>
          <h1 className="page-title">Long Descriptions</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Long Descriptions</li>
          </ol>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Product Long Descriptions Management</div>
              {longDescTotal > 0 && (
                <div className="ms-auto">
                  <Typography variant="body2" color="text.secondary">
                    Total: {longDescTotal} items
                  </Typography>
                </div>
              )}
            </div>
            <div className="card-body">
              <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search by SKU or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button type="submit" variant="contained" size="small">
                          Search
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {loadingLongDesc ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>SKU</TableCell>
                          <TableCell>Descriptions</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {longDescriptions?.map((desc) => (
                          <TableRow key={desc.SKU} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                {desc.SKU}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {desc.longDesc_en && (
                                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                    <strong>EN:</strong> {truncateText(desc.longDesc_en)}
                                  </Typography>
                                )}
                                {desc.longDesc_fi && (
                                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                    <strong>FIN:</strong> {truncateText(desc.longDesc_fi)}
                                  </Typography>
                                )}
                                {desc.longDesc_fr && (
                                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                    <strong>FR:</strong> {truncateText(desc.longDesc_fr)}
                                  </Typography>
                                )}
                                {desc.longDesc_de && (
                                  <Typography variant="caption" display="block">
                                    <strong>DE:</strong> {truncateText(desc.longDesc_de)}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={hasAnyDescription(desc) ? 'Has Content' : 'Empty'}
                                color={hasAnyDescription(desc) ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(desc)}
                                title="Edit description"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(desc.SKU)}
                                title="Delete description"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {longDescPageCount > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                      <Pagination
                        count={longDescPageCount}
                        page={longDescPage}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}

              {!loadingLongDesc && (!longDescriptions || longDescriptions.length === 0) && (
                <Box textAlign="center" py={5}>
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm ? 'No descriptions found matching your search.' : 'No long descriptions available.'}
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>

      <EditLongDescDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        description={selectedDescription}
        onSave={handleEditSave}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Long Description</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the long description for SKU: <strong>{selectedSKU}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} color="inherit" />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Main>
  );
};

export default LongDescriptions;